import {
  CloudRounded,
  SecurityRounded,
  StorageRounded,
} from "@mui/icons-material";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import { Box, Typography } from "@mui/material";
import CustomCopyButton from "../../../../../../components/CustomCopyButton";
import SvgToIcon from "../../../../../../icons/SvgToIcon";
import { icons } from "../../../icons/iconExport";
import { DataStoreResourceTypeIconsAndLabels } from "../../../utils";
import TextIntegrationStyle from "../../TextIntegrationStyle";

const AzureBlobContainerDetailsView = ({ entityData }) => {
  const containerData = entityData?.azure_blob_container_data;

  if (!containerData) {
    return null;
  }

  const [container_logo, container_label] = Object.keys(
    DataStoreResourceTypeIconsAndLabels
  ).includes(containerData.resource_type)
    ? DataStoreResourceTypeIconsAndLabels[containerData.resource_type]
    : [null, null];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Container Name */}
      {containerData.container_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.databaseIcon} width="16px" />
            <Typography variant="subtitle2">Container Name</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={containerData.container_name}
            />
          </Box>
        </Box>
      )}

      {/* Storage Account */}
      {containerData.storage_account_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <StorageRounded sx={{ width: "16px" }} />
            <Typography variant="subtitle2">Storage Account</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={containerData.storage_account_name}
            />
          </Box>
        </Box>
      )}

      {/* Resource Type */}
      {containerData.resource_type && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.serverTypeIcon} width="16px" />
            <Typography variant="subtitle2">Resource Type</Typography>
          </Box>
          <Box ml={2} display="flex" alignItems="center" gap={1}>
            {container_logo && (
              <SvgToIcon IconLogo={container_logo} width="16px" />
            )}
            <TextIntegrationStyle
              title=""
              value={container_label || containerData.resource_type}
            />
          </Box>
        </Box>
      )}

      {/* Resource ID */}
      {containerData.resource_id && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.serialNumberIcon} width="16px" />
            <Typography variant="subtitle2">Resource ID</Typography>
          </Box>
          <Box ml={2} display="flex" alignItems="center" gap={2}>
            <TextIntegrationStyle title="" value={containerData.resource_id} />
            <CustomCopyButton
              copyTitle="Resource ID"
              copyText={containerData.resource_id}
            />
          </Box>
        </Box>
      )}

      {/* Public Access */}
      {containerData?.container_public_access !== undefined && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.publicIcon} width="16px" />
            <Typography variant="subtitle2">Container Public Access</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={
                containerData?.container_public_access
                  ? containerData?.storage_account_allows_blob_public_access
                    ? "True"
                    : "Blocked by Storage Account"
                  : "False"
              }
            />
          </Box>
        </Box>
      )}

      {/* Security Settings */}
      {(containerData.has_immutability_policy !== undefined ||
        containerData.has_legal_hold !== undefined ||
        containerData.immutable_storage_with_versioning_enabled !==
          undefined) && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SecurityRounded sx={{ width: "16px" }} />
            <Typography variant="subtitle2">Security Settings</Typography>
          </Box>
          <Box ml={2} display="flex" flexDirection="column" gap={1}>
            {containerData.has_immutability_policy !== undefined && (
              <TextIntegrationStyle
                title="Immutability Policy:"
                value={containerData.has_immutability_policy ? "Yes" : "No"}
              />
            )}
            {containerData.has_legal_hold !== undefined && (
              <TextIntegrationStyle
                title="Legal Hold:"
                value={containerData.has_legal_hold ? "Yes" : "No"}
              />
            )}
            {containerData.immutable_storage_with_versioning_enabled !==
              undefined && (
              <TextIntegrationStyle
                title="Immutable Storage with Versioning:"
                value={
                  containerData.immutable_storage_with_versioning_enabled
                    ? "Enabled"
                    : "Disabled"
                }
              />
            )}
          </Box>
        </Box>
      )}

      {/* Resource Group */}
      {containerData.resource_group_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <FolderOpenRoundedIcon sx={{ width: "16px" }} />
            <Typography variant="subtitle2">Resource Group</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={containerData.resource_group_name}
            />
          </Box>
        </Box>
      )}

      {/* Location */}
      {containerData.location && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.regionServerIcon} width="16px" />
            <Typography variant="subtitle2">Location</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle title="" value={containerData.location} />
          </Box>
        </Box>
      )}

      {/* Subscription */}
      {containerData.subscription_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <CloudRounded sx={{ width: "16px" }} />
            <Typography variant="subtitle2">Subscription</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={containerData.subscription_name}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AzureBlobContainerDetailsView;
